import {Helmet} from '@dr.pogodin/react-helmet';

import {useParams} from 'react-router-dom';

import {isNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {useGetArticleCategoryQuery} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsTemplate, SettingsTemplateHeader} from '@dms/settings/shared';
import {usePermissions} from '@dms/shared';

import {ArticleCategoryDetailForm} from './components/ArticleCategoryDetailForm';

export type WarehousesArticleCategoryForm = {
  name?: string;
  code?: string | null;
  active: boolean;
  isDefault: boolean;
};

export function WarehouseArticleCategoryDetail() {
  const {id} = useParams();

  const [warehouseDictionaryEdit, warehouseDictionaryCreate] = usePermissions({
    permissionKeys: ['warehouseDictionaryEdit', 'warehouseDictionaryCreate'],
  });

  const {
    data: articleCategory,
    isLoading,
    isError,
  } = useGetArticleCategoryQuery({articleCategoryId: id as string}, {skip: isNilOrEmpty(id)});

  // Check edit permission only if id is not nil that means that the user is creating a new measurement unit
  const isEditDisabled = isNil(id) ? !warehouseDictionaryCreate : !warehouseDictionaryEdit;

  const header: SettingsTemplateHeader = {
    title: isNil(articleCategory)
      ? i18n.t('entity.warehouse.labels.newWarehouseCategory')
      : (articleCategory?.name ?? ''),
    breadcrumbs: [
      {
        label: i18n.t('entity.warehouse.labels.warehouseCategory'),
        href: settingsRoutes.warehousesArticleCategory,
      },
    ],
    alerts: isEditDisabled
      ? [
          {
            type: 'banner',
            variant: 'info',
            message: i18n.t('module.warehouse.label.missingWarehouseDictionaryEditPermission'),
          },
        ]
      : undefined,
  };

  return (
    <>
      <Helmet title={i18n.t('entity.warehouse.labels.articleCategoryDetail')} />
      <SettingsTemplate
        header={header}
        isLoading={isLoading}
        isError={isError}
        data-testid={testIds.settings.warehousesArticleCategoryDetail('template')}
      >
        <ArticleCategoryDetailForm
          id={id}
          articleCategory={articleCategory}
          isEditDisabled={isEditDisabled}
          data-testid={testIds.settings.warehousesArticleCategory('articleCategoryDetailForm')}
        />
      </SettingsTemplate>
    </>
  );
}
